import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Copy, KeyRound, Loader2 } from "lucide-react";

import { Field, FieldCard } from "@/components/brand/SettingsShell";
import { Pill, type PillTone } from "@/components/brand/Pill";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { CreatedProjectApiKey, ProjectApiKey } from "@/api/project-api-keys";
import {
  useCreateProjectApiKey,
  useProjectApiKeys,
  useRenewProjectApiKey,
  useRevokeProjectApiKey,
} from "@/hooks/useProjectApiKeys";
import { useToast } from "@/hooks/use-toast";
import { errorDetailOr } from "@/lib/api-error";
import { formatDate } from "@/lib/datetime";

/** Validades oferecidas; a api aceita de 1 a 365 dias, e 90 é o padrão dela (#1783). */
const VALID_DAYS = [30, 90, 180, 365] as const;
const DEFAULT_VALID_DAYS = 90;

const STATUS_TONE: Record<ProjectApiKey["status"], PillTone> = {
  active: "ok",
  expired: "muted",
  revoked: "danger",
};

const STATUS_LABEL: Record<ProjectApiKey["status"], string> = {
  active: "Active",
  expired: "Expired",
  revoked: "Revoked",
};

/**
 * As chaves de API do projeto, que o ex-kit-server usa para carregar os dados do cliente (#1783).
 *
 * A chave em claro aparece uma vez, na criação ou na renovação: o banco guarda só o hash. Renovar
 * gera outra chave com o mesmo nome e as mesmas permissões e revoga a antiga.
 */
export function ProjectApiKeysCard({ id, projectId }: { id: string; projectId: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data: keys, isLoading } = useProjectApiKeys(projectId);
  const createKey = useCreateProjectApiKey(projectId);
  const revokeKey = useRevokeProjectApiKey(projectId);
  const renewKey = useRenewProjectApiKey(projectId);
  const [label, setLabel] = useState("");
  const [validDays, setValidDays] = useState<number>(DEFAULT_VALID_DAYS);
  const [shownKey, setShownKey] = useState<CreatedProjectApiKey | null>(null);
  const [pendingRevoke, setPendingRevoke] = useState<ProjectApiKey | null>(null);

  const fail = (title: string) => (err: unknown) =>
    toast({ variant: "destructive", title, description: errorDetailOr(err, t("Try again.")) });

  const handleCreate = async () => {
    if (!label.trim()) return;
    try {
      const created = await createKey.mutateAsync({ label: label.trim(), valid_days: validDays });
      setLabel("");
      setShownKey(created);
    } catch (err) {
      fail(t("Could not create the key"))(err);
    }
  };

  const handleRenew = async (key: ProjectApiKey) => {
    try {
      setShownKey(await renewKey.mutateAsync({ keyId: key.id, validDays }));
    } catch (err) {
      fail(t("Could not renew the key"))(err);
    }
  };

  const handleRevoke = async () => {
    if (!pendingRevoke) return;
    try {
      await revokeKey.mutateAsync(pendingRevoke.id);
      setPendingRevoke(null);
      toast({ title: t("Key revoked"), description: t("The extraction kit can no longer use it.") });
    } catch (err) {
      fail(t("Could not revoke the key"))(err);
    }
  };

  const copyKey = async () => {
    if (!shownKey) return;
    try {
      await navigator.clipboard.writeText(shownKey.key);
      toast({ title: t("Key copied") });
    } catch {
      toast({ variant: "destructive", title: t("Could not copy; select the key and copy it.") });
    }
  };

  return (
    <FieldCard
      id={id}
      icon={<KeyRound />}
      title={t("API keys")}
      sub={t("Keys the extraction kit server uses to load this project's data.")}
    >
      <Field label={t("New key")} hint={t("The key is shown once, right after it is created.")}>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            className="max-w-xs"
            value={label}
            maxLength={120}
            placeholder={t("e.g. ex-kit-server production")}
            aria-label={t("Key name")}
            onChange={(event) => setLabel(event.target.value)}
          />
          <Select value={String(validDays)} onValueChange={(next) => setValidDays(Number(next))}>
            <SelectTrigger className="w-36" aria-label={t("Validity")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VALID_DAYS.map((days) => (
                <SelectItem key={days} value={String(days)}>
                  {t("{{n}} days", { n: days })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="action"
            onClick={handleCreate}
            disabled={!label.trim() || createKey.isPending}
          >
            {createKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t("Create key")}
          </Button>
        </div>
      </Field>

      <div className="pt-4">
        {isLoading ? (
          <p className="text-xs text-muted-foreground">{t("Loading…")}</p>
        ) : !keys?.length ? (
          <p className="text-xs text-muted-foreground">{t("No API key yet.")}</p>
        ) : (
          <table className="w-full text-xs" aria-label={t("API keys")}>
            <thead>
              <tr className="text-left text-[#596b85]">
                <th className="py-2 font-medium">{t("Name")}</th>
                <th className="py-2 font-medium">{t("Prefix")}</th>
                <th className="py-2 font-medium">{t("Status")}</th>
                <th className="py-2 font-medium">{t("Expires")}</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id} className="border-t border-[#e8ebf0]">
                  <td className="py-2">{key.label}</td>
                  <td className="py-2 font-mono">{key.key_prefix}</td>
                  <td className="py-2">
                    <Pill tone={STATUS_TONE[key.status]}>{t(STATUS_LABEL[key.status])}</Pill>
                  </td>
                  <td className="py-2">{formatDate(key.expires_at)}</td>
                  <td className="py-2 text-right space-x-2 whitespace-nowrap">
                    {key.status === "active" && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRenew(key)}
                          disabled={renewKey.isPending}
                        >
                          {t("Renew")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPendingRevoke(key)}
                          disabled={revokeKey.isPending}
                        >
                          {t("Revoke")}
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!shownKey} onOpenChange={(open) => !open && setShownKey(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Copy the key now")}</DialogTitle>
            <DialogDescription>
              {t("myGROW keeps only a hash of it: after this window closes, nobody can see it again.")}
            </DialogDescription>
          </DialogHeader>
          <code className="block break-all rounded bg-[#f4f6f9] p-3 font-mono text-xs" data-testid="new-api-key">
            {shownKey?.key}
          </code>
          <DialogFooter>
            <Button variant="outline" className="gap-2" onClick={copyKey}>
              <Copy className="h-4 w-4" /> {t("Copy")}
            </Button>
            <Button onClick={() => setShownKey(null)}>{t("Done")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pendingRevoke} onOpenChange={(open) => !open && setPendingRevoke(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Revoke this key?")}</DialogTitle>
            <DialogDescription>
              {t("The extraction kit stops loading with it on its next request. This cannot be undone.")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingRevoke(null)}>
              {t("Cancel")}
            </Button>
            <Button variant="destructive" onClick={handleRevoke} disabled={revokeKey.isPending}>
              {t("Revoke")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FieldCard>
  );
}
